// AuthGuard / GuardedButton — 路由 / 按钮级登录守卫,对齐 docs/ui/user-page-flows.md §4-§8。
//
// user-page-flows.md §4.1 四态分类:
//   🟢 Public  — 无登录可访问;loading 不阻塞
//   🔒 Authed  — 必须登录;访问时 if (!session) → /login?next=<path>
//   🔀 Mixed   — 公共可见 + 内部按钮触发登录(用 GuardedButton 而非页面守卫)
//
// `next` query param 与现有 user/admin/agent/service login.tsx 一致(`params.get('next') ?? '/'`)。

import type { ReactNode, ButtonHTMLAttributes, MouseEvent } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useCurrentUser } from './use-current-user'

// ────────────────────────── RequireAuth(路由守卫)

export type RequireAuthProps = {
  /** loading 时显示的内容(默认 null,与 user-page-flows §4.1 "loading 不阻塞" 一致) */
  loading?: ReactNode
  /** 未登录时跳转的 login 路径(默认 /auth/login) */
  loginPath?: string
  children: ReactNode
}

export function RequireAuth({
  loading = null,
  loginPath = '/auth/login',
  children,
}: RequireAuthProps) {
  const { status } = useCurrentUser()
  const location = useLocation()

  if (status === 'loading') return <>{loading}</>
  if (status === 'unauthenticated') {
    const next = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`${loginPath}?next=${next}`} replace />
  }
  return <>{children}</>
}

// ────────────────────────── GuardedButton(按钮级守卫)

export type GuardedButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /**
   * true:点击前检查 session;无 → 跳 login + next
   * false / undefined:正常 onClick
   */
  requireAuth?: boolean
  loginPath?: string
}

export function GuardedButton({
  requireAuth,
  loginPath = '/auth/login',
  onClick,
  children,
  ...rest
}: GuardedButtonProps) {
  const { status } = useCurrentUser()
  const navigate = useNavigate()
  const location = useLocation()

  const handle = (e: MouseEvent<HTMLButtonElement>) => {
    if (requireAuth && status !== 'authenticated') {
      e.preventDefault()
      const next = encodeURIComponent(location.pathname + location.search)
      navigate(`${loginPath}?next=${next}`)
      return
    }
    onClick?.(e)
  }

  return (
    <button {...rest} onClick={handle}>
      {children}
    </button>
  )
}
